/**
 * Pure calendar-date math for the Week View (Phase 0).
 *
 * Locked rule (resolves the spec↔plan contradiction):
 *  - The official test date is the fixed ANCHOR. The plan's final week
 *    (`totalWeeks`) is the ISO week containing the anchor.
 *  - `planStartMonday = mondayOf(anchor) − (totalWeeks − 1) weeks`.
 *  - A day's date = `planStartMonday + (week−1)*7 + (day_of_week−1)` days,
 *    where `day_of_week` is ISO (1=Mon … 7=Sun).
 *  - Fallback anchor when no test date (e.g. pregnancy plans):
 *    `generated_at`, else `today`.
 *
 * The taper/rest buffer near the test (`daysUntilTest − 3` in generation) is a
 * plan-content concern, not a date-enumeration concern; this module takes
 * `totalWeeks` as given. All arithmetic is local date-only (no UTC drift).
 */

export interface PlanDayLite {
  day_of_week: number
  is_rest: boolean
}

export interface PlanDateInput {
  totalWeeks: number
  weekTemplate: PlanDayLite[]
  officialTestDate: string
  generatedAt: string
  planStartDate?: string
}

export interface PlanDateEntry {
  week: number
  dayOfWeek: number
  date: string // YYYY-MM-DD (local)
  isRest: boolean
  isTestDate: boolean
}

function parseISODateOnly(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const da = Number(m[3])
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(da)) return null
  return new Date(y, mo - 1, da)
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}

function mondayOnOrBeforeDate(d: Date): Date {
  const dow = d.getDay() // 0=Sun … 6=Sat
  const deltaToMonday = (dow + 6) % 7 // Mon→0, Tue→1, … Sun→6
  return addDays(d, -deltaToMonday)
}

/** Format a Date as YYYY-MM-DD using its LOCAL components. */
export function isoToYMD(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

/** Monday (ISO) on or before the given YYYY-MM-DD string. */
export function mondayOnOrBefore(iso: string): Date {
  const d = parseISODateOnly(iso)
  if (!d) throw new Error(`Invalid date: ${iso}`)
  return mondayOnOrBeforeDate(d)
}

/** ISO day_of_week (1=Mon ... 7=Sun) for the given YYYY-MM-DD. */
export function getDayOfWeek(iso: string): number {
  const d = parseISODateOnly(iso)
  if (!d) return 1
  return ((d.getDay() + 6) % 7) + 1
}

/** Resolve the anchor date: test date → generatedAt → today. */
export function resolveAnchor(
  plan: { officialTestDate: string; generatedAt: string },
  today: string,
): Date {
  return (
    parseISODateOnly(plan.officialTestDate) ??
    parseISODateOnly(plan.generatedAt) ??
    parseISODateOnly(today) ??
    (() => { throw new Error('No resolvable anchor date') })()
  )
}

/** Anchor inputs needed to place a plan on the calendar (no template). */
export interface AnchorInput {
  totalWeeks: number
  officialTestDate: string
  generatedAt: string
  planStartDate?: string  // if present, plan's first workout date; enables mid-week start + calendar Mon-Sun weeks (partial first week ok)
}

/** Plan-start Monday: the Monday of the anchor week, minus (totalWeeks−1) weeks. */
function planStartMonday(input: AnchorInput, today: string): Date {
  return addDays(mondayOnOrBeforeDate(resolveAnchor(input, today)), -(input.totalWeeks - 1) * 7)
}

/**
 * Calendar date (YYYY-MM-DD, local) for a given week + ISO day_of_week.
 * Depends only on week + dayOfWeek (not the template), so it is correct for
 * both phase-1 and phase-2 weeks. Single source for all plan date arithmetic.
 * If planStartDate is provided, uses the Monday of the calendar week containing the plan's first workout,
 * then offsets by week and dow. This supports plans that begin mid-week (partial first calendar week, Mon-Sun alignment).
 */
export function dateForDay(input: AnchorInput, week: number, dayOfWeek: number, today: string): string {
  if (input.planStartDate) {
    const startD = parseISODateOnly(input.planStartDate)
    if (startD) {
      const startMonday = mondayOnOrBeforeDate(startD)
      const weekMonday = addDays(startMonday, (week - 1) * 7)
      return isoToYMD(addDays(weekMonday, dayOfWeek - 1))
    }
  }
  return isoToYMD(addDays(planStartMonday(input, today), (week - 1) * 7 + (dayOfWeek - 1)))
}

/** Enumerate a calendar date for every (week, templated day) in the plan. */
export function enumeratePlanDates(plan: PlanDateInput, today: string): PlanDateEntry[] {
  const testDate = parseISODateOnly(plan.officialTestDate)
  const testYMD = testDate ? isoToYMD(testDate) : null

  const out: PlanDateEntry[] = []
  for (let week = 1; week <= plan.totalWeeks; week++) {
    for (const day of plan.weekTemplate) {
      const ymd = dateForDay(plan, week, day.day_of_week, today)
      out.push({
        week,
        dayOfWeek: day.day_of_week,
        date: ymd,
        isRest: day.is_rest,
        isTestDate: testYMD !== null && ymd === testYMD,
      })
    }
  }
  return out
}
